import type { ViewStyle } from 'react-native'
import { getCssTransform } from './css-transform'

type KeyframeState = {
  durationMs: number
  delayMs: number
  easingFn?: (value: number) => number
  callback?: (finished: boolean) => void
}

export type KeyframeDefinitions = Record<number, ViewStyle> & {
  from?: ViewStyle
  to?: ViewStyle
}

type WaapiKeyframe = {
  offset: number
  opacity?: number
  transform?: string
}

function normalizeKeyframeDefinitions(definitions: KeyframeDefinitions): Record<number, ViewStyle> {
  const normalized: Record<number, ViewStyle> = {}

  if (definitions.from) {
    normalized[0] = definitions.from
  }

  if (definitions.to) {
    normalized[100] = definitions.to
  }

  for (const [key, style] of Object.entries(definitions)) {
    if (key === 'from' || key === 'to' || !style) {
      continue
    }

    const percent = Number(key)

    if (Number.isNaN(percent)) {
      continue
    }

    normalized[percent] = style
  }

  return normalized
}

function getState(keyframe: Keyframe): KeyframeState {
  return {
    callback: keyframe.callback,
    delayMs: keyframe.delayMs,
    durationMs: keyframe.durationMs,
    easingFn: keyframe.easingFn,
  }
}

// Native Keyframe в web-shim нет — те же кадры проигрываем через Web Animations API.
export class Keyframe {
  readonly definitions: Record<number, ViewStyle>
  readonly durationMs: number
  readonly delayMs: number
  readonly easingFn?: (value: number) => number
  readonly callback?: (finished: boolean) => void

  constructor(definitions: KeyframeDefinitions, state: Partial<KeyframeState> = {}) {
    this.definitions = normalizeKeyframeDefinitions(definitions)
    this.durationMs = state.durationMs ?? 500
    this.delayMs = state.delayMs ?? 0
    this.easingFn = state.easingFn
    this.callback = state.callback
  }

  duration(durationMs: number) {
    return new Keyframe(this.definitions, { ...getState(this), durationMs })
  }

  delay(delayMs: number) {
    return new Keyframe(this.definitions, { ...getState(this), delayMs })
  }

  easing(easingFn: (value: number) => number) {
    return new Keyframe(this.definitions, { ...getState(this), easingFn })
  }

  withCallback(callback: (finished: boolean) => void) {
    return new Keyframe(this.definitions, { ...getState(this), callback })
  }
}

export function isKeyframe(value: unknown): value is Keyframe {
  return value instanceof Keyframe
}

export function getKeyframeFrameStyle(animation: Keyframe, percent: number): ViewStyle {
  return animation.definitions[percent] ?? {}
}

export function getWaapiKeyframes(animation: Keyframe, baseTransform?: string): WaapiKeyframe[] {
  return Object.entries(animation.definitions)
    .map(([key, style]) => {
      const frame: WaapiKeyframe = { offset: Number(key) / 100 }

      if (typeof style.opacity === 'number') {
        frame.opacity = style.opacity
      }

      const transform = getCssTransform(style.transform)

      if (transform) {
        frame.transform = baseTransform ? `${baseTransform} ${transform}` : transform
      } else if (baseTransform) {
        frame.transform = baseTransform
      }

      return frame
    })
    .filter((frame) => !Number.isNaN(frame.offset))
    .sort((left, right) => left.offset - right.offset)
}

export function playWebKeyframeAnimation(
  element: HTMLElement,
  animation: Keyframe,
  baseTransform?: string,
): Animation | null {
  if (typeof element.animate !== 'function') {
    return null
  }

  return element.animate(getWaapiKeyframes(animation, baseTransform), {
    delay: animation.delayMs,
    duration: animation.durationMs,
    easing: 'linear',
    fill: 'both',
  })
}
